/**
 * Reports the choices a client makes inside the floor-plan configurator.
 *
 * Legacy measured this form more closely than any other — five separate events
 * for the colour, the render type, the template, "create from scratch" and the
 * logo — because it is the one form where the client makes a series of
 * decisions rather than filling fields. All five are kept.
 *
 * The logo is the odd one out and the reason this is a hook rather than a
 * function: legacy fired it when the uploaded-logo count went UP, not when an
 * upload was started, so a failed upload was never counted as one. Mirroring
 * that needs the previous count, and the count has to be seeded from the first
 * render or a resumed draft would announce its restored logo as a fresh upload.
 */

import { useCallback, useEffect, useRef } from 'react'

import type { CartItemId } from 'models/purchaseFlowRevamp'
import type { FloorPlanChoice } from '../../analytics/events'
import { PfRevampEventName } from '../../analytics/events'
import { dispatchPfRevampEvent } from '../../analytics/usePfRevampAnalytics'

interface Announcer {
  /** Announce a choice that is not the logo. */
  announce: (choice: Exclude<FloorPlanChoice, 'logo'>, value?: string, renderType?: string) => void
}

export const useFloorPlanChoiceAnnouncer = (
  cartItemId: CartItemId,
  /** How many logos the hub currently holds. */
  logoCount: number
): Announcer => {
  const announce = useCallback<Announcer['announce']>((choice, value, renderType) => {
    dispatchPfRevampEvent({
      type: PfRevampEventName.FLOOR_PLAN_CHOICE_MADE,
      payload: { cartItemId, choice, value, renderType },
    })
  }, [cartItemId])

  // Seeded with the count at mount, so a restored logo is a starting state and
  // not an upload the client just performed.
  const previousLogoCount = useRef(logoCount)

  useEffect(() => {
    const grew = logoCount > previousLogoCount.current
    previousLogoCount.current = logoCount
    if (!grew) return

    dispatchPfRevampEvent({
      type: PfRevampEventName.FLOOR_PLAN_CHOICE_MADE,
      payload: { cartItemId, choice: 'logo' },
    })
  }, [logoCount, cartItemId])

  return { announce }
}
